import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ChevronLeft,
  ChevronRight,
  Minimize2,
  ExternalLink,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  Download,
  File,
  FileSpreadsheet,
  Image,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import type { PurchaseRequest, PurchaseRequestStatus, PurchaseRequestLine, Document } from '@/types/purchase-request';
import type { FinOpsPurchaseRequestDetail } from '@/types/finops.types';

interface PurchaseRequestFullModalProps {
  purchaseRequest: PurchaseRequest | null;
  allPurchaseRequests: PurchaseRequest[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPurchaseRequestChange: (pr: PurchaseRequest) => void;
  onMinimize?: () => void;
  projectCode?: string;
  instanceId?: string;
  onFetchLineDetails?: (projectCode: string, instanceId: string) => Promise<FinOpsPurchaseRequestDetail[] | null>;
}

function mapFinOpsDetailToLine(detail: FinOpsPurchaseRequestDetail): PurchaseRequestLine {
  return {
    id: `finops-line-${detail.primaryKey}`,
    lineNumber: detail.lineNumber,
    lineType: detail.lineType || detail.type || 'Price',
    description: detail.description || '',
    quantity: detail.quantity || 0,
    unitPrice: detail.unitPrice,
    totalAmount: detail.total || detail.value || detail.gross || 0,
    currency: detail.currencyCode || detail.currSymbol || 'GBP',
    costCentreCode: detail.costCentreCode || '',
    costCentreName: detail.costCentreName,
    unitOfMeasure: detail.unitOfMeasure,
    taxCode: detail.taxCode || detail.vatCode,
    taxAmount: detail.taxAmount || detail.vat,
    projectTaskCode: detail.projectTask,
    glAccount: detail.glAccount,
    notes: detail.notes,
  };
}

const statusConfig: Record<string, {
  variant: 'success' | 'warning' | 'destructive' | 'default';
  accent: 'success' | 'warning' | 'destructive' | 'info';
  icon: typeof CheckCircle2;
  color: string;
}> = {
  Approved: {
    variant: 'success',
    accent: 'success',
    icon: CheckCircle2,
    color: 'text-green-600',
  },
  Pending: {
    variant: 'warning',
    accent: 'warning',
    icon: Clock,
    color: 'text-orange-600',
  },
  Rejected: {
    variant: 'destructive',
    accent: 'destructive',
    icon: XCircle,
    color: 'text-red-600',
  },
  Posted: {
    variant: 'default',
    accent: 'info',
    icon: CheckCircle2,
    color: 'text-blue-600',
  },
  Redirected: {
    variant: 'warning',
    accent: 'warning',
    icon: AlertCircle,
    color: 'text-orange-600',
  },
  Reopened: {
    variant: 'warning',
    accent: 'warning',
    icon: AlertCircle,
    color: 'text-yellow-600',
  },
  Cancelled: {
    variant: 'destructive',
    accent: 'destructive',
    icon: XCircle,
    color: 'text-gray-600',
  },
  Draft: {
    variant: 'default',
    accent: 'info',
    icon: FileText,
    color: 'text-gray-600',
  },
};

// Helper function to get status config with fallback for unknown statuses
const getStatusConfig = (status: string) => {
  return statusConfig[status] || {
    variant: 'default' as const,
    accent: 'info' as const,
    icon: AlertCircle,
    color: 'text-gray-600',
  };
};

const getDocumentIcon = (type: Document['type']) => {
  switch (type) {
    case 'pdf':
      return FileText;
    case 'image':
      return Image;
    case 'spreadsheet':
      return FileSpreadsheet;
    case 'document':
      return FileText;
    default:
      return File;
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const formatCurrency = (amount: number, currency: string = 'GBP') => {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    }).format(amount) + ` ${currency}`;
  } catch {
    // Fallback for invalid currency codes (e.g., "DEUR")
    return `${amount.toFixed(2)} ${currency}`;
  }
};

export function PurchaseRequestFullModal({
  purchaseRequest,
  allPurchaseRequests,
  open,
  onOpenChange,
  onPurchaseRequestChange,
  onMinimize,
  projectCode,
  instanceId,
  onFetchLineDetails,
}: PurchaseRequestFullModalProps) {
  const [activeTab, setActiveTab] = useState('details');
  const [showDocuments, setShowDocuments] = useState(false);
  const [lineItems, setLineItems] = useState<PurchaseRequestLine[]>([]);
  const [lineItemsLoading, setLineItemsLoading] = useState(false);
  const [lineItemsError, setLineItemsError] = useState<string | null>(null);
  const [lineItemsFetched, setLineItemsFetched] = useState(false);

  // Reset tab when modal opens or PR changes
  useEffect(() => {
    if (open) {
      setActiveTab('details');
      setShowDocuments(false);
      setLineItems([]);
      setLineItemsError(null);
      setLineItemsFetched(false);
    }
  }, [open, purchaseRequest?.id]);

  // Fetch line items when Line Items tab is selected
  useEffect(() => {
    const fetchLineItems = async () => {
      if (activeTab !== 'lines' || lineItemsFetched || !purchaseRequest) {
        return;
      }

      // If purchase request already has lines, use them
      if (purchaseRequest.lines && purchaseRequest.lines.length > 0) {
        setLineItems(purchaseRequest.lines);
        setLineItemsFetched(true);
        return;
      }

      // If we have the callback and required params, fetch from FinOps
      if (onFetchLineDetails && projectCode && instanceId) {
        setLineItemsLoading(true);
        setLineItemsError(null);
        try {
          const details = await onFetchLineDetails(projectCode, instanceId);
          if (details && details.length > 0) {
            // Filter details by the current purchase request's headerKey
            // The purchaseRequest.id is in format "finops-{primaryKey}"
            const headerKey = purchaseRequest.id.startsWith('finops-') 
              ? parseInt(purchaseRequest.id.replace('finops-', ''), 10)
              : null;
            
            const filteredDetails = headerKey !== null
              ? details.filter(d => d.headerKey === headerKey)
              : details;
            
            const mappedLines = filteredDetails.map(mapFinOpsDetailToLine);
            setLineItems(mappedLines);
          } else {
            setLineItems([]);
          }
        } catch (error) {
          console.error('[PurchaseRequestFullModal] Error fetching line details:', error);
          setLineItemsError('Failed to load line items');
        } finally {
          setLineItemsLoading(false);
          setLineItemsFetched(true);
        }
      } else {
        setLineItemsFetched(true);
      }
    };

    fetchLineItems();
  }, [activeTab, lineItemsFetched, purchaseRequest, projectCode, instanceId, onFetchLineDetails]);

  // Navigation
  const currentIndex = purchaseRequest
    ? allPurchaseRequests.findIndex((pr) => pr.id === purchaseRequest.id)
    : -1;

  const canNavigatePrevious = currentIndex > 0;
  const canNavigateNext = currentIndex < allPurchaseRequests.length - 1;

  const handleNavigatePrevious = () => {
    if (canNavigatePrevious) {
      onPurchaseRequestChange(allPurchaseRequests[currentIndex - 1]);
    }
  };

  const handleNavigateNext = () => {
    if (canNavigateNext) {
      onPurchaseRequestChange(allPurchaseRequests[currentIndex + 1]);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open || !purchaseRequest) return;

      if (e.key === 'ArrowLeft' && canNavigatePrevious) {
        handleNavigatePrevious();
      } else if (e.key === 'ArrowRight' && canNavigateNext) {
        handleNavigateNext();
      } else if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, purchaseRequest, canNavigatePrevious, canNavigateNext]);

  if (!purchaseRequest) return null;

  const config = getStatusConfig(purchaseRequest.status);
  const StatusIcon = config.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[600px] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5" />
              <DialogTitle>{purchaseRequest.requestId}</DialogTitle>
              <Badge
                variant={config.variant}
                accent={config.accent}
                className="gap-1"
              >
                <StatusIcon className="h-3 w-3" />
                {purchaseRequest.status}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {currentIndex + 1} of {allPurchaseRequests.length}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleNavigatePrevious}
                  disabled={!canNavigatePrevious}
                  title="Previous (←)"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleNavigateNext}
                  disabled={!canNavigateNext}
                  title="Next (→)"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              {onMinimize && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={onMinimize}
                  title="Return to sheet view"
                >
                  <Minimize2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4 flex-1 flex flex-col overflow-hidden">
          <TabsList>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="lines">Line Items</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-6 mt-6 overflow-y-auto flex-1">
            {/* Key Information */}
            <div className="grid gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Description</label>
                <p className="mt-1 text-sm">{purchaseRequest.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Amount</label>
                  <p className="mt-1 text-lg font-semibold">
                    {formatCurrency(purchaseRequest.amount, purchaseRequest.currency)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Currency</label>
                  <p className="mt-1 text-sm font-medium">{purchaseRequest.currency}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Date</label>
                  <p className="mt-1 text-sm">{format(new Date(purchaseRequest.date), 'dd MMMM yyyy')}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Category</label>
                  <p className="mt-1 text-sm">{purchaseRequest.category}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Supplier</label>
                  <p className="mt-1 text-sm">{purchaseRequest.supplier}</p>
                </div>
                {purchaseRequest.costCentreCode && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Cost Centre</label>
                    <p className="mt-1 text-sm">
                      <span className="font-medium">{purchaseRequest.costCentreCode}</span>
                      {purchaseRequest.costCentreName && (
                        <span className="text-muted-foreground"> - {purchaseRequest.costCentreName}</span>
                      )}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">Requested By</label>
                <p className="mt-1 text-sm">
                  {purchaseRequest.requestedBy} on {format(new Date(purchaseRequest.date), 'dd MMM yyyy')}
                </p>
              </div>

              {purchaseRequest.approvedBy && purchaseRequest.approvedDate && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Approved By</label>
                  <p className="mt-1 text-sm">
                    {purchaseRequest.approvedBy} on {format(new Date(purchaseRequest.approvedDate), 'dd MMM yyyy')}
                  </p>
                </div>
              )}

              {purchaseRequest.notes && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Notes</label>
                  <p className="mt-1 text-sm">{purchaseRequest.notes}</p>
                </div>
              )}
            </div>

            <Separator />

            {/* Documents Section */}
            <div>
              <Button
                variant="outline"
                className="w-full justify-between"
                onClick={() => setShowDocuments(!showDocuments)}
                disabled={!purchaseRequest.documents || purchaseRequest.documents.length === 0}
              >
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {purchaseRequest.documents && purchaseRequest.documents.length > 0
                    ? `View Documents (${purchaseRequest.documents.length})`
                    : 'No documents available'}
                </span>
                {purchaseRequest.documents && purchaseRequest.documents.length > 0 && (
                  showDocuments ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                )}
              </Button>

              {showDocuments && purchaseRequest.documents && purchaseRequest.documents.length > 0 && (
                <div className="mt-4 space-y-2">
                  {purchaseRequest.documents.map((document) => {
                    const DocumentIcon = getDocumentIcon(document.type);
                    return (
                      <div
                        key={document.id}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="flex-shrink-0">
                            <DocumentIcon className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{document.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(document.size)} • {format(new Date(document.uploadDate), 'dd MMM yyyy')}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="flex-shrink-0"
                          asChild
                        >
                          <a
                            href={document.url}
                            download={document.name}
                            onClick={(e) => {
                              e.preventDefault();
                              console.log('Downloading:', document.name);
                              alert(`Mock download: ${document.name}`);
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <Separator />

            {/* Action Button */}
            <Button className="w-full" variant="outline" asChild>
              <a
                href={`#/financials/purchase-requests/${purchaseRequest.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                View in Access Financials Operations
              </a>
            </Button>
          </TabsContent>

          <TabsContent value="lines" className="mt-6 flex-1 overflow-hidden">
            {lineItemsLoading ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Loading line items...</p>
              </div>
            ) : lineItemsError ? (
              <div className="text-center py-8 text-destructive">
                <AlertCircle className="h-6 w-6 mx-auto mb-2" />
                <p>{lineItemsError}</p>
              </div>
            ) : lineItems.length > 0 ? (
              <div className="border rounded-lg h-full overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px]">Line #</TableHead>
                      <TableHead className="w-[100px]">Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right w-[100px]">Quantity</TableHead>
                      <TableHead className="text-right w-[120px]">Unit Price</TableHead>
                      <TableHead className="text-right w-[150px]">Total Amount</TableHead>
                      <TableHead className="w-[100px]">Tax Code</TableHead>
                      <TableHead className="w-[120px]">GL Account</TableHead>
                      <TableHead className="w-[150px]">Cost Centre</TableHead>
                      <TableHead className="min-w-[200px]">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineItems.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell className="font-medium">{line.lineNumber}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{line.lineType}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[300px]">{line.description}</TableCell>
                        <TableCell className="text-right">
                          {line.lineType === 'Text' ? '-' : line.quantity}
                        </TableCell>
                        <TableCell className="text-right">
                          {line.lineType === 'Text' || !line.unitPrice
                            ? '-' 
                            : formatCurrency(line.unitPrice, line.currency)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {line.lineType === 'Text' 
                            ? '-' 
                            : formatCurrency(line.totalAmount, line.currency)}
                        </TableCell>
                        <TableCell>
                          {line.taxCode ? (
                            <Badge variant="secondary">{line.taxCode}</Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {line.glAccount ? (
                            <span className="font-mono text-sm">{line.glAccount}</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{line.costCentreCode}</p>
                            {line.costCentreName && (
                              <p className="text-xs text-muted-foreground">{line.costCentreName}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {line.notes ? (
                            <span className="text-sm">{line.notes}</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No line items available
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
